/**
 * Call History Search Datasource (Infrastructure)
 *
 * Handles raw API communication for call history search operations.
 * Maps v2 backend responses to frontend domain types.
 */

import { get, post } from '@/shared/api';

import type { V2CallResponse } from '@/shared/services/socketio/types';
import type { CallHistoryItem } from '../../domain/types/responses';
import type { CallHistorySearchRequest } from '../../domain/types/requests';
import type { CallHistorySearchResponse, CallHistorySearchOptionsResponse } from '../../domain/types/responses';

// ============================================================================
// v2 Response Types & Mapper
// ============================================================================

/**
 * v2 call history item extends CallResponse with extra fields
 */
interface V2CallHistoryItemResponse extends V2CallResponse {
  outcome?: string;
  tone?: string;
  difficulty?: string;
  duration?: string;
  channel?: string;
}

/**
 * Map v2 CallHistoryItemResponse to CallHistoryItem
 */
function mapV2CallToHistoryItem(call: V2CallHistoryItemResponse): CallHistoryItem {
  const callee = call.callees?.[0];
  return {
    id: call.id,
    case_id: call.case_info?.case_id,
    loan_id: call.case_info?.loan_id,
    call_type: call.type,
    call_status: call.status,
    call_outcome: call.outcome,
    caller: call.caller
      ? {
          id: call.caller.id,
          name: call.caller.name,
          photo_url: call.caller.photo_url,
          role: call.caller.role,
        }
      : undefined,
    callee: callee
      ? {
          id: callee.id,
          name: callee.name,
          photo_url: callee.photo_url,
          role: callee.role,
        }
      : undefined,
    case_call_detail: {
      call_outcome: call.outcome,
      tone: call.tone,
      difficulty_rating: call.difficulty,
    },
    call_time_start: call.time_start,
    call_time_end: call.time_end,
    call_duration: call.duration,
  };
}

// ============================================================================
// Datasource Interface
// ============================================================================

export interface CallHistorySearchDatasource {
  searchCallHistory(request: CallHistorySearchRequest): Promise<CallHistorySearchResponse>;
  getSearchOptions(): Promise<CallHistorySearchOptionsResponse>;
}

// ============================================================================
// Datasource Implementation
// ============================================================================

const CALL_HISTORY_URL = '/app/queue/history/search';

export class CallHistorySearchDatasourceImpl implements CallHistorySearchDatasource {
  async searchCallHistory(request: CallHistorySearchRequest): Promise<CallHistorySearchResponse> {
    const apiRequest = {
      page: request.page,
      per_page: request.per_page,
      search: request.search
        ? {
            text: request.search.text,
            column: request.search.column,
          }
        : undefined,
      filters: request.filters.map(f => ({
        column: f.column,
        values: f.values,
        range: f.range
          ? {
              lower_bound: f.range.lower_bound,
              upper_bound: f.range.upper_bound,
            }
          : undefined,
      })),
      sorts: request.sorts.map(s => ({
        column: s.column,
        order: s.order,
      })),
    };

    // v2 returns CallHistoryItemResponse items — map to CallHistoryItem
    const response = await post<{ items: V2CallHistoryItemResponse[]; total: number; page: number; per_page: number }>(
      CALL_HISTORY_URL,
      apiRequest
    );

    return {
      items: response.items.map(mapV2CallToHistoryItem),
      total: response.total,
      page: response.page,
      per_page: response.per_page,
    };
  }

  async getSearchOptions(): Promise<CallHistorySearchOptionsResponse> {
    const response = await get<CallHistorySearchOptionsResponse>(CALL_HISTORY_URL);
    return response;
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const callHistorySearchDatasource: CallHistorySearchDatasource = new CallHistorySearchDatasourceImpl();
